import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  ClipboardCheck,
  FileSearch,
  FileText,
  Gauge,
  PoundSterling,
  Route,
  ShieldCheck,
  UploadCloud,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/waitlist/Footer';
import { Navbar } from '../components/waitlist/Navbar';
import { getFleetTrialSignupUrl } from '../config/urls';
import { usePageMeta } from '../hooks/usePageMeta';
import { Analytics } from '../services/analytics';

const featurePillars = [
  {
    title: 'AI PCN intake and review',
    detail: 'Upload images or PDFs, let AI extract PCN fields, then review low-confidence fields before saving verified tickets.',
    icon: UploadCloud,
  },
  {
    title: 'Driver and hire matching',
    detail: 'Connect a PCN to the right vehicle, driver, and hire period so responsibility is visible before the next action.',
    icon: Users,
  },
  {
    title: 'Liability readiness',
    detail: 'Check hire evidence, liability clauses, signatures, and driver details before preparing transfer or follow-up work.',
    icon: ShieldCheck,
  },
  {
    title: 'Deadline risk',
    detail: 'Surface discount, transfer, appeal, charge certificate, order-for-recovery, and statutory declaration windows.',
    icon: AlertTriangle,
  },
  {
    title: 'FleetMind recommendations',
    detail: 'Get context-aware next-action guidance for transfer, appeal, pay, investigate, invoice, recover, or close.',
    icon: Gauge,
  },
  {
    title: 'Recovery and admin fees',
    detail: 'Track PCN cost recovery, admin-fee visibility, driver invoice status, disputes, paid items, and write-offs.',
    icon: PoundSterling,
  },
] as const;

const workflowGroups = [
  {
    label: 'PCO hire operator workflow',
    title: 'For hire companies that need to prove who had the vehicle.',
    description:
      'This workflow focuses on driver matching, hire-period evidence, liability readiness, transfer preparation, and driver recovery.',
    items: [
      ['AI PCN intake', 'Extract PCN number, VRM, dates, amount, issuer, location, and stage for review.'],
      ['Hire-period matching', 'Match the notice to the driver and hire agreement active at the contravention time.'],
      ['Evidence-gap checks', 'Flag missing signed agreement, liability clause, driver address, or supporting records.'],
      ['Transfer support', 'Prepare reviewed PCN, driver, hire, and evidence details for a transfer-of-liability workflow.'],
      ['Admin-fee recovery', 'Track whether PCN cost and admin fees are pending, paid, disputed, waived, or written off.'],
    ],
    highlight: true,
  },
  {
    label: 'Owner-managed fleet workflow',
    title: 'For operators keeping responsibility inside the business.',
    description:
      'This workflow focuses on ticket control, appeal/pay/recharge decisions, driver risk, internal accountability, and reporting.',
    items: [
      ['Fleet PCN dashboard', 'Track all tickets across owned or managed vehicles from one workspace.'],
      ['Vehicle and driver records', 'Maintain vehicle details, driver profiles, history, and risk indicators.'],
      ['Appeal and pay guidance', 'Use FleetMind to support appeal, pay, investigate, internal recharge, or close decisions.'],
      ['Deadline monitoring', 'Keep discount, appeal, escalation, and enforcement deadlines visible.'],
      ['Reports and exports', 'Use per-PCN reports, trial snapshots, aggregate reporting, and CSV exports.'],
    ],
    highlight: false,
  },
] as const;

const auditFeatures = [
  {
    title: 'Submission records',
    detail: 'Log transfer, appeal, or representation submissions with method, proof type, screenshots, references, and status.',
    icon: FileSearch,
  },
  {
    title: 'Per-PCN reports',
    detail: 'See readiness, liability, deadline, recovery, and evidence summaries per ticket, then export CSV when needed.',
    icon: FileText,
  },
  {
    title: 'Growth+ aggregate reports',
    detail: 'Review liability, appeal, authority, repeat driver/location, exposure, and recovery trends by date range.',
    icon: BarChart3,
  },
] as const;

const planFeatures = [
  ['Starter', 'AI extraction, bulk upload, risk scoring, manual liability transfer, basic dashboard, and per-PCN reporting.'],
  ['Growth', 'Adds DVLA lookup, advanced reports, AI appeals, one-click transfer support, agreement audit, and shared team seats.'],
  ['Pro Fleet', 'Adds higher-volume soft limits, 10 team seats, stronger audit depth, evidence controls, and bulk workflows.'],
  ['Concierge', 'Adds unlimited seats, custom support terms, and managed onboarding for complex operations.'],
] as const;

export function FleetFeatures() {
  usePageMeta({
    title: 'FleetMode Features - AI-First PCN Control for PCO Hire Companies',
    description:
      'Explore PCNGuard FleetMode features for AI PCN intake, driver and hire matching, liability readiness, deadline risk, FleetMind recommendations, driver recovery, reports, and audit trails.',
    canonical: 'https://pcnguard.co.uk/fleet/features',
    ogTitle: 'FleetMode features for AI-first PCN control',
    ogDescription:
      'See the PCO hire and fleet operator workflows inside PCNGuard FleetMode: AI intake, liability readiness, deadline risk, recovery, reports, and audit trails.',
  });

  const trialUrl = getFleetTrialSignupUrl();

  const trackClick = (placement: string) => {
    Analytics.track('fleet_features_cta_clicked', {
      placement,
      destination: trialUrl,
      audience: 'pco_hire',
    });
  };

  return (
    <div className="min-h-screen bg-[#fffaf1] text-[#081b1f] selection:bg-[#d9a45b]/25 font-sans">
      <Navbar />

      <main id="main-content">
        <section className="border-b border-[#d7c3a8] bg-[#f4eadb] px-4 pb-14 pt-24 sm:px-6 sm:pb-18 sm:pt-28">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.88fr_1.12fr] lg:items-center">
            <div>
              <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                FleetMode features
              </div>
              <h1 className="mt-5 font-space text-4xl font-black leading-tight tracking-tight text-[#081b1f] sm:text-5xl">
                Features for AI-first PCN control, from intake to recovery.
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-[#596764] sm:text-lg">
                PCNGuard FleetMode gives PCO hire and fleet operators a structured workflow for AI intake, driver and hire matching, liability readiness, deadline risk, FleetMind guidance, recovery, reports, and audit trails.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={trialUrl}
                  onClick={() => trackClick('hero')}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#194d3a] px-6 py-3 font-bold text-white shadow-lg shadow-[#194d3a]/20 transition-all duration-200 hover:bg-[#26654e] active:translate-y-px"
                >
                  Check 3 PCNs free
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/fleet"
                  className="inline-flex items-center justify-center rounded-md border border-[#d6c7b6] bg-white px-6 py-3 font-bold text-[#344347] transition-all duration-200 hover:border-[#194d3a] hover:text-[#194d3a]"
                >
                  View FleetMode
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-[#d7c3a8] bg-[#fffaf1] p-5 shadow-[0_24px_80px_rgba(8,27,31,0.14)] sm:p-6">
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ['AI intake', 'Extract and review'],
                  ['Liability', 'Check readiness'],
                  ['Deadlines', 'Flag risk'],
                  ['Recovery', 'Track status'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border border-[#e4d8c8] bg-[#f6f0e6] p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#8a5c22]">{label}</div>
                    <div className="mt-1 font-space text-xl font-bold text-[#081b1f]">{value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-md border border-[#b5d5c4] bg-[#eaf3ee] p-4">
                <div className="flex items-start gap-3">
                  <Route className="mt-0.5 h-5 w-5 shrink-0 text-[#194d3a]" />
                  <p className="text-sm leading-6 text-[#344347]">
                    The feature set splits cleanly into two operator paths: PCO hire teams proving driver liability, and fleet operators controlling internal pay, appeal, recharge, and reporting decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                Core feature pillars
              </div>
              <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                The features your PCN team uses every day.
              </h2>
              <p className="mt-4 text-base leading-7 text-[#596764]">
                These are grounded in the app workflow: upload, verify, match, decide, submit, recover, and report.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {featurePillars.map((feature) => (
                <article key={feature.title} className="rounded-lg border border-[#e4d8c8] bg-[#f6f0e6] p-5 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]">
                    <feature.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-space text-lg font-bold text-[#081b1f]">{feature.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#596764]">{feature.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#081b1f] px-4 py-14 text-white sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 grid gap-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-white/15 px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#d9a45b]">
                  Workflow classification
                </div>
                <h2 className="mt-5 font-space text-3xl font-bold leading-tight sm:text-4xl">
                  PCO hire and fleet operators need different answers.
                </h2>
              </div>
              <p className="text-base leading-7 text-[#c8d2cf]">
                FleetMode supports both workflows without blurring them: hire companies need evidence and recovery control; owner-managed fleets need internal accountability and next-action clarity.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {workflowGroups.map((group) => (
                <article key={group.label} className={`rounded-lg border p-5 shadow-sm sm:p-6 ${group.highlight ? 'border-[#d9a45b]/50 bg-white/[0.08]' : 'border-white/10 bg-white/[0.045]'}`}>
                  <div className="inline-flex rounded-md border border-white/15 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-[#d9a45b]">
                    {group.label}
                  </div>
                  <h3 className="mt-4 font-space text-2xl font-bold text-white">{group.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#c8d2cf]">{group.description}</p>
                  <div className="mt-5 grid gap-3">
                    {group.items.map(([name, detail]) => (
                      <div key={name} className="border border-white/10 bg-white/[0.06] p-4">
                        <div className="font-space font-bold text-white">{name}</div>
                        <p className="mt-1 text-sm leading-6 text-[#c8d2cf]">{detail}</p>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.78fr_1.22fr]">
            <div>
              <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                Reporting and proof
              </div>
              <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                Keep an audit trail after the decision.
              </h2>
              <p className="mt-4 text-base leading-7 text-[#596764]">
                FleetMode is not just a feature checklist. It keeps records around submissions, per-PCN readiness, trial snapshots, and management reporting.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {auditFeatures.map((feature) => (
                <article key={feature.title} className="rounded-lg border border-[#e4d8c8] bg-[#f6f0e6] p-5 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]">
                    <feature.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-space text-lg font-bold text-[#081b1f]">{feature.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#596764]">{feature.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f6f0e6] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                  Plan depth
                </div>
                <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                  Choose by workflow depth, not PCN volume.
                </h2>
              </div>
              <Link to="/fleet#pricing" className="inline-flex items-center gap-2 font-bold text-[#194d3a] hover:text-[#26654e]">
                See pricing
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {planFeatures.map(([name, detail]) => (
                <article key={name} className="rounded-lg border border-[#e4d8c8] bg-[#fffaf1] p-5 shadow-sm">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-[#8a5c22]">FleetMode {name}</div>
                  <p className="mt-3 text-sm leading-6 text-[#596764]">{detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#eaf3ee] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-5xl rounded-lg border border-[#b5d5c4] bg-[#fffaf1] p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#194d3a]">
                  <ClipboardCheck className="h-4 w-4" />
                  Product-led proof
                </div>
                <h2 className="mt-3 font-space text-3xl font-bold">See the workflow on three real PCNs.</h2>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-[#596764]">
                  The free check shows AI intake, liability readiness, evidence gaps, deadline risk, and recovery visibility before a wider rollout.
                </p>
              </div>
              <a
                href={trialUrl}
                onClick={() => trackClick('final')}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-[#194d3a] px-6 py-3 font-bold text-white shadow-lg shadow-[#194d3a]/20 transition-all duration-200 hover:bg-[#26654e] active:translate-y-px"
              >
                Check 3 PCNs free
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default FleetFeatures;
